import { z } from "zod";
import { FieldExtensionComponentProps, makeFieldSchema } from "@backstage/plugin-scaffolder-react";
import { ErrorPanel } from "@backstage/core-components";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { extractValueFromFormData, substituteTemplate } from "../helpers";

const uiOptionsSchema = z.object({
    jmesPath: z.string().describe('The path to the value to extract.'),
    readonly: z.boolean().optional().describe('Whether the field is read-only').default(false),
});

export const ArraySelectorFieldSchema = makeFieldSchema({
    output: zImpl => zImpl.union([zImpl.string(), zImpl.array(zImpl.string())]),
    uiOptions: zImpl => zImpl.object({
        jmesPath: zImpl.string().describe('The path to the value to extract.'),
        readonly: zImpl.boolean().optional().describe('Whether the field is read-only').default(false),
    }),
});

export const ArraySelectorField = (props: FieldExtensionComponentProps<string[] | string, z.infer<typeof uiOptionsSchema>>) => {
    const { onChange, formData } = props;
    const { formData: allFormData } = props.formContext;

    const uiOptions = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);
    if (!uiOptions.success) {
        return <ErrorPanel error={uiOptions.error} />;
    }

    const { jmesPath: sourcePath, readonly } = uiOptions.data;

    // Substitute template variables in the sourcePath
    const { result: resolvedPath, isValid: pathValid, missingProperties: pathMissingProps } = substituteTemplate<string>(
        sourcePath,
        allFormData,
        (value) => String(value)
    );


    const { value: sourceValue, exists: sourceExists } = pathValid
        ? extractValueFromFormData(resolvedPath, allFormData)
        : { value: null, exists: false };

    const isArray = props.schema.type === "array";

    let arrayOptions: string[] = [];
    if (sourceExists && sourceValue) {
        if (Array.isArray(sourceValue)) {
            arrayOptions = (sourceValue.map((value) => String(value)));
        } else {
            arrayOptions = [sourceValue];
        }
    }

    const fieldTitle = props.schema.title ?? props.name;
    const fieldDescription = props.schema.description;
    let displayValue: string[];
    if (Array.isArray(formData)) {
        displayValue = formData;
    } else if (formData) {
        displayValue = [formData];
    } else {
        displayValue = [];
    }
    // Show error if path template has missing properties
    if (!pathValid && pathMissingProps.length > 0) {
        return (<FormControl
            margin="normal"
            required={props.required}
            error
        >
            <TextField
                label={fieldTitle}
                value=""
                disabled
                error
            />
            <FormHelperText error>
                Missing required properties for path: {pathMissingProps.join(', ')}
            </FormHelperText>
        </FormControl>)
    }

    return (
        <FormControl>
            <InputLabel>{fieldTitle}</InputLabel>
            {isArray ? (
                <Select
                    multiple
                    value={displayValue}
                    label={fieldTitle}
                    onChange={(e) => onChange(e.target.value as string[])}
                    disabled={readonly}
                    input={<OutlinedInput label={fieldTitle} />}
                    renderValue={(selected) => (
                        <Box>
                            {selected.map((value) => (
                                <Chip key={value} label={value} />
                            ))}
                        </Box>
                    )}
                >
                    {arrayOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                            <Checkbox checked={displayValue.includes(name)} />
                            <ListItemText primary={name} />
                        </MenuItem>
                    ))}
                </Select>
            ) : (
                <Select
                    value={displayValue}
                    label={fieldTitle}
                    onChange={(e) => onChange(e.target.value as string)}
                    disabled={readonly}
                    input={<OutlinedInput label={fieldTitle} />}
                >
                    {arrayOptions.map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                </Select>
            )}
            <FormHelperText>{fieldDescription}</FormHelperText >
        </FormControl>
    );
}
